import {
  CURRENCY_MULTIPLIERS,
  type Currency,
  type CurrencyMultiplier,
  currencyUnitName,
  DEFAULT_CURRENCY,
  formatAmountWithUnit,
  parseCurrencyMultiplier,
  resolveCurrency,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { CurrencyIcon } from "@loot-os/ui/components/currency-icon";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { Tabs, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useState } from "react";
import { type CurrencyPreset, currencyPreset } from "../lib/currency";
import { useStorefrontConfig } from "../lib/storefront";
import { useTenantRole } from "../lib/useTenantRole";
import { CurrencyIconField } from "./StorefrontFields";

/**
 * The unsaved currency. One object rather than a state per field because the preset couples
 * them: choosing Credits writes every field at once. `iconSvg` is null until touched, so an
 * untouched icon is never re-validated (a stored one could otherwise block every other edit).
 */
interface CurrencyDraft {
  mode: CurrencyPreset;
  name: string;
  namePlural: string;
  symbol: string;
  multiplier: CurrencyMultiplier;
  iconSvg: string | null;
}

interface StoredCurrency {
  currencyName: string;
  currencyNamePlural: string | null;
  currencySymbol: string;
  currencyMultiplier: number;
  currencyIconSvg: string;
}

function draftFrom(config: StoredCurrency): CurrencyDraft {
  return {
    mode: currencyPreset(resolveCurrency(config)),
    name: config.currencyName,
    namePlural: config.currencyNamePlural ?? "",
    symbol: config.currencySymbol,
    multiplier: parseCurrencyMultiplier(config.currencyMultiplier),
    iconSvg: null,
  };
}

/** The currency the draft describes, as the storefront would resolve it. */
function toCurrency(draft: CurrencyDraft, storedIconSvg: string): Currency {
  if (draft.mode === "credits") return DEFAULT_CURRENCY;
  return resolveCurrency({
    currencyName: draft.name.trim() || "unit",
    currencyNamePlural: draft.namePlural.trim() || null,
    currencySymbol: draft.symbol.trim(),
    currencyMultiplier: draft.multiplier,
    currencyIconSvg: draft.iconSvg ?? storedIconSvg,
  });
}

/**
 * How balances and prices read on this storefront, a card on Storefront → Payments beside the
 * packs whose credited amounts it names. Amounts are stored in cents either way, so switching
 * changes how they look, never their value. Sends only its own columns; `storefront.update` is
 * a plain patch, so a sibling card's columns are never clobbered.
 */
export function CurrencyCard() {
  const [draft, setDraft] = useState<CurrencyDraft | null>(null);
  const { query, update } = useStorefrontConfig({ onSaved: () => setDraft(null) });
  const { can } = useTenantRole();

  // Narrowing only: the layout renders the page once the same query has data.
  if (!query.data) return null;

  const { config } = query.data;
  const current = draft ?? draftFrom(config);
  const candidate = toCurrency(current, config.currencyIconSvg);
  const nameOk = current.mode === "credits" || current.name.trim().length > 0;
  const singular = current.name.trim() || "coin";

  const choosePreset = (mode: CurrencyPreset) => {
    if (mode === "credits") {
      setDraft({
        mode,
        name: DEFAULT_CURRENCY.name,
        namePlural: "",
        symbol: DEFAULT_CURRENCY.symbol,
        multiplier: DEFAULT_CURRENCY.multiplier,
        // An icon is a custom-currency thing: clear a stored one explicitly, touch nothing otherwise.
        iconSvg: config.currencyIconSvg ? "" : null,
      });
    } else {
      // Custom starts from whatever is stored, so a tenant coming back to it finds their values.
      setDraft({ ...current, mode });
    }
  };

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameOk) return;
    // Only this section's fields: storefront.update is a plain patch, so a sibling section's
    // columns are never clobbered. A blank plural stores null and readers derive `${name}s`.
    update.mutate({
      currencyName: candidate.name,
      currencyNamePlural: current.mode === "credits" ? "" : current.namePlural.trim(),
      currencySymbol: candidate.symbol,
      currencyMultiplier: candidate.multiplier,
      ...(current.iconSvg === null ? {} : { currencyIconSvg: current.iconSvg }),
    });
  };

  return (
    <Card id="currency">
      <CardHeader>
        <CardTitle>Currency</CardTitle>
        <CardDescription>
          How balances and prices read on this storefront. Amounts are stored in cents either way,
          so switching changes how they look, never their value.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-5" onSubmit={save}>
          <div className="grid gap-3">
            <Tabs
              value={current.mode}
              onValueChange={(value) => choosePreset(value as CurrencyPreset)}
            >
              <TabsList>
                <TabsTrigger value="credits">Credits ($)</TabsTrigger>
                <TabsTrigger value="custom">Custom currency</TabsTrigger>
              </TabsList>
            </Tabs>
            <p className="text-sm text-muted-foreground">
              {current.mode === "credits"
                ? "Credits read as dollars: 1,250 cents is $12.50, and nothing but the amount is shown."
                : "Name the unit and say how many cents make one. Without a symbol the unit name is shown after the amount."}
            </p>
          </div>

          {current.mode === "custom" && (
            <div className="grid gap-4 border-t pt-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="currency-name">Name (singular)</Label>
                  <Input
                    id="currency-name"
                    type="text"
                    value={current.name}
                    onChange={(e) => setDraft({ ...current, name: e.target.value })}
                    placeholder="coin"
                    maxLength={50}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="currency-name-plural">Plural (optional)</Label>
                  <Input
                    id="currency-name-plural"
                    type="text"
                    value={current.namePlural}
                    onChange={(e) => setDraft({ ...current, namePlural: e.target.value })}
                    placeholder={`${singular}s`}
                    maxLength={50}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="currency-symbol">Symbol (optional)</Label>
                  <Input
                    id="currency-symbol"
                    type="text"
                    value={current.symbol}
                    onChange={(e) => setDraft({ ...current, symbol: e.target.value })}
                    placeholder="none"
                    maxLength={3}
                    className="w-28"
                  />
                  <p className="text-xs text-muted-foreground">
                    Written right before the amount, up to 3 characters. Blank reads "
                    {formatAmountWithUnit(1250, { ...candidate, symbol: "" })}".
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="currency-multiplier">
                    Cents per {currencyUnitName(candidate.multiplier, candidate)}
                  </Label>
                  <Select
                    value={String(current.multiplier)}
                    onValueChange={(value) =>
                      setDraft({
                        ...current,
                        multiplier: parseCurrencyMultiplier(Number(value)),
                      })
                    }
                  >
                    <SelectTrigger id="currency-multiplier" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_MULTIPLIERS.map((multiplier) => (
                        <SelectItem key={multiplier} value={String(multiplier)}>
                          {multiplier} · 1,250 cents →{" "}
                          {formatAmountWithUnit(1250, { ...candidate, multiplier })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    How many cents one {candidate.name} is worth; decides the decimals shown.
                  </p>
                </div>
              </div>
              <div className="max-w-xl">
                <CurrencyIconField
                  value={current.iconSvg ?? config.currencyIconSvg}
                  onChange={(svg) => setDraft({ ...current, iconSvg: svg })}
                  currency={candidate}
                />
              </div>
            </div>
          )}

          <div className="grid gap-2 border-t pt-5">
            <span className="text-sm font-medium">Preview</span>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
              <span className="flex items-center gap-2">
                <span className="text-muted-foreground">Balance</span>
                <span className="flex items-center gap-1 font-mono">
                  <CurrencyIcon currency={candidate} className="size-4" />
                  {formatAmountWithUnit(12_345, candidate)}
                </span>
              </span>
              <span className="flex items-center gap-2">
                <span className="text-muted-foreground">Box price</span>
                <span className="flex items-center gap-1 font-mono">
                  <CurrencyIcon currency={candidate} className="size-4" />
                  {formatAmountWithUnit(250, candidate)}
                </span>
              </span>
              <span className="text-xs text-muted-foreground">
                {formatAmountWithUnit(candidate.multiplier, candidate)} ·{" "}
                {formatAmountWithUnit(candidate.multiplier * 2, candidate)}
              </span>
            </div>
          </div>

          <div>
            <Button type="submit" disabled={update.isPending || !can("editor") || !nameOk}>
              {update.isPending ? "Saving…" : "Save currency"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
